import type { CreateLocalRoundInput } from "./round-sync-engine.js";

export type SeasonRoundSetupInput = Omit<CreateLocalRoundInput, "roundId">;
export interface SeasonRoundSelection {
  seasonId: string;
  slotId: string;
}
export interface PendingSeasonRound extends SeasonRoundSelection {
  storageVersion: 1;
  accountId: string;
  roundId: string;
  input: SeasonRoundSetupInput;
  createdAt: string;
  phase: "draft" | "binding" | "starting" | "started" | "abandoning";
}
export interface SeasonIntentStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}
const key = (accountId: string) =>
  `fairway-rivalry:season-round-start:${accountId}`;

/** One recoverable setup per account; completed receipt prevents same-slot double starts. */
export class SeasonRoundStartStore {
  public constructor(private readonly storage: SeasonIntentStorage) {}
  public read(accountId: string): PendingSeasonRound | null {
    const raw = this.storage.getItem(key(accountId));
    if (!raw) return null;
    let value: PendingSeasonRound;
    try {
      value = JSON.parse(raw) as PendingSeasonRound;
    } catch {
      throw new Error(
        "The saved season setup cannot be read. Do not start a replacement round.",
      );
    }
    if (
      !value ||
      value.storageVersion !== 1 ||
      value.accountId !== accountId ||
      typeof value.roundId !== "string" ||
      typeof value.seasonId !== "string" ||
      typeof value.slotId !== "string" ||
      !Array.isArray(value.input?.participants) ||
      !["draft", "binding", "starting", "started", "abandoning"].includes(
        value.phase,
      )
    )
      throw new Error(
        "The saved season setup is invalid. Do not start a replacement round.",
      );
    return value;
  }
  public write(intent: PendingSeasonRound): void {
    this.storage.setItem(key(intent.accountId), JSON.stringify(intent));
  }
  public clear(accountId: string): void {
    this.storage.removeItem(key(accountId));
  }
  public pending(accountId: string): PendingSeasonRound | null {
    const intent = this.read(accountId);
    return intent?.phase === "started" ? null : intent;
  }
}

export function clearSeasonRoundIntent(
  accountId: string,
  storage: SeasonIntentStorage = localStorage,
): void {
  new SeasonRoundStartStore(storage).clear(accountId);
}
